import styled from "@emotion/styled";
import { useAtomValue } from "jotai";
import type { FunctionComponent } from "react";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";

import type { ContentSchemaRenderProps } from "scmp-app/components/schema-render/content";
import { rosettaAtom } from "scmp-app/lib/rosetta";
import type { hooksInlinePlusWidgetContent$key } from "scmp-app/queries/__generated__/hooksInlinePlusWidgetContent.graphql";

import { InlinePlusWidgetContents } from "./contents";
import { useInlinePlusWidget } from "./hooks";
import { InlinePlusWidgetNonSubscriber } from "./non-subscriber";
import { InlinePlusWidgetPlaceholder } from "./placeholder";
import { Container } from "./styles";

type Props = ContentSchemaRenderProps & {
  reference?: hooksInlinePlusWidgetContent$key;
};

const Component: FunctionComponent<Props> = ({ reference }) => {
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const hasPianoExperienceExecuted = asyncRosettaState?.result?.hasPianoExperienceExecuted ?? false;
  const hasScmpPlusAccessRight = asyncRosettaState?.result?.hasScmpPlusAccessRight ?? false;
  // Not showing the plus inline widget if the first topic is not in the list of selected topics
  const { shouldShowInlinePlusWidget } = useInlinePlusWidget(reference);

  if (!shouldShowInlinePlusWidget) return null;

  if (!hasPianoExperienceExecuted)
    return (
      <Container>
        <InlinePlusWidgetPlaceholder />
      </Container>
    );

  if (!hasScmpPlusAccessRight)
    return (
      <Container>
        <InlinePlusWidgetNonSubscriber />
      </Container>
    );

  return (
    <ClientSideSuspense
      fallback={
        <Container>
          <InlinePlusWidgetPlaceholder />
        </Container>
      }
    >
      <InlinePlusWidgetContents reference={reference} />
    </ClientSideSuspense>
  );
};

Component.displayName = "InlinePlusWidget";

export const InlinePlusWidget = styled(Component)``;
